// app/javascript/components/UserManagement.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { t } from '../utils/i18n';

const UserManagement = ({ onBack, onNavigate, api }) => {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('email-asc');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [openActionDropdownId, setOpenActionDropdownId] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
    fetchLocations();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.actions-dropdown-container')) {
        setOpenActionDropdownId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await api.get('/users');
      setUsers(data.users);
    } catch (err) {
      console.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await api.get('/departments');
      setDepartments(data);
    } catch (err) {
      console.error('Failed to fetch departments');
    }
  };

  const fetchLocations = async () => {
    try {
      const data = await api.get('/locations');
      setLocations(data);
    } catch (err) {
      console.error('Failed to fetch locations');
    }
  };

  const handleAssignDepartment = async (userId) => {
    setActiveDropdown(activeDropdown === `dept-${userId}` ? null : `dept-${userId}`);
  };

  const handleAssignLocation = async (userId) => {
    setActiveDropdown(activeDropdown === `loc-${userId}` ? null : `loc-${userId}`);
  };


  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter(user =>
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.army_personnel?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.roles?.some(role => role.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const [field, order] = sortBy.split('-');
    return filtered.sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      if (field === 'id') {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [users, searchTerm, sortBy]);

  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setDropdownOpen(false);
  };

  const handleDelete = async (id) => {
    if (confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${id}`);
        setUsers(users.filter(u => u.id !== id));
      } catch (err) {
        alert('Failed to delete user');
      }
    }
  };

  const assignDepartment = async (userId, departmentId) => {
    try {
      await api.patch(`/army_personnel/${userId}/assign_department`, { department_id: departmentId });
      fetchUsers();
      setActiveDropdown(null);
    } catch (err) {
      alert('Failed to assign department');
    }
  };

  const assignLocation = async (userId, locationId) => {
    try {
      await api.patch(`/army_personnel/${userId}/assign_location`, { location_id: locationId });
      fetchUsers();
      setActiveDropdown(null);
    } catch (err) {
      alert('Failed to assign location');
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <>
      <div className="mb_40 baseTableWrap">
        <div className="dFlex baseTableHd mb_16">
          <h2 className="txt20 fwMedium colorBlack">User Management ({filteredAndSortedUsers.length})</h2>
          <div className="dFlex baseTableTR">
            {/* <button className="btnAdd me-3" onClick={() => onNavigate('add-user')}>
              Add User
            </button> */}
            <div className="searchBox">
              <input
                type="text"
                placeholder="Search by Email, Name, Role"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="button">
                <img src="/images/search1.png" alt="" />
              </button>
            </div>
            <div className="dropdown dropdownSortBy">
              <button
                className="dropdown-toggle"
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {t('sort_by')}
              </button>
              <ul className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('email-asc')}>Email A-Z</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('email-desc')}>Email Z-A</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('created_at-desc')}>Newest First</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('created_at-asc')}>Oldest First</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="baseTableContainer">
          <table className="baseTable">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Email</th>
                <th>Personnel Name</th>
                <th>Roles</th>
                <th>{t('sidebar.location')}</th>
                <th>{t('department')}</th>
                <th>{t('created_date')}</th>
                <th style={{ minWidth: "175px" }}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedUsers.length > 0 ? (
                filteredAndSortedUsers.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.email}</td>
                    <td>{user.army_personnel?.full_name || 'Not assigned'}</td>
                    <td>{user.roles?.map(role => role.name).join(', ') || 'No roles'}</td>
                    <td>
                      {activeDropdown === `loc-${user.id}` ? (
                        <select
                          className="form-control"
                          value={user.location?.id || ''}
                          onChange={(e) => assignLocation(user.id, e.target.value)}
                        >
                          <option value="">Select Location</option>
                          {locations.map(loc => (
                            <option key={loc.id} value={loc.id}>
                              {loc.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        user.location?.name || 'N/A'
                      )}
                    </td>
                    <td>
                      {activeDropdown === `dept-${user.id}` ? (
                        <select
                          className="form-control"
                          value={user.department?.id || ''}
                          onChange={(e) => assignDepartment(user.id, e.target.value)}
                        >
                          <option value="">Select Department</option>
                          {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>
                              {dept.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        user.department?.name || 'N/A'
                      )}
                    </td>
                    <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : ''}</td>
                    <td>
                      <div className="actions-dropdown-container" style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            fontWeight: '600',
                            borderRadius: '4px',
                            border: '1px solid #cbd5e1',
                            backgroundColor: '#ffffff',
                            color: '#334155',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            cursor: 'pointer'
                          }}
                          onClick={() => setOpenActionDropdownId(openActionDropdownId === user.id ? null : user.id)}
                        >
                          Actions <span style={{ fontSize: '10px', marginLeft: '2px' }}>▼</span>
                        </button>
                        {openActionDropdownId === user.id && (
                          <ul
                            className="dropdown-menu show"
                            style={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              zIndex: 1000,
                              minWidth: '160px',
                              padding: '4px 0',
                              margin: '4px 0 0',
                              fontSize: '13px',
                              backgroundColor: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              listStyle: 'none'
                            }}
                          >
                            <li>
                              <button
                                className="dropdown-item"
                                style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
                                onClick={() => {
                                  onNavigate('edit-user', user.id);
                                  setOpenActionDropdownId(null);
                                }}
                              >
                                <img src="/images/edit.png" alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }} /> Edit User
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item"
                                style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
                                onClick={() => {
                                  handleAssignDepartment(user.id);
                                  setOpenActionDropdownId(null);
                                }}
                              >
                                <img src="/images/edit.png" alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }} /> Assign Dept
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item"
                                style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
                                onClick={() => {
                                  handleAssignLocation(user.id);
                                  setOpenActionDropdownId(null);
                                }}
                              >
                                <img src="/images/edit.png" alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }} /> Assign Location
                              </button>
                            </li>
                            <li style={{ borderTop: '1px solid #f1f5f9', marginTop: '4px', paddingTop: '4px' }}>
                              <button
                                className="dropdown-item text-danger"
                                style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626' }}
                                onClick={() => {
                                  handleDelete(user.id);
                                  setOpenActionDropdownId(null);
                                }}
                              >
                                <img src="/images/delete.png" alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }} /> Delete
                              </button>
                            </li>
                          </ul>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                    No users found matching "{searchTerm}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default UserManagement;
